import { isToday, isTomorrow } from 'date-fns'
import type { AppointmentStatus, InvoiceStatus } from './types'

const localeTags = { en: 'en-US', es: 'es-US', 'pt-BR': 'pt-BR', ja: 'ja-JP' } as const

export function activeLocale() {
  if (typeof window === 'undefined') return 'en-US'
  const saved = window.localStorage.getItem('tidyping-locale') as keyof typeof localeTags | null
  if (saved && localeTags[saved]) return localeTags[saved]
  const browser = window.navigator.language
  if (browser.startsWith('pt')) return 'pt-BR'
  if (browser.startsWith('es')) return 'es-US'
  if (browser.startsWith('ja')) return 'ja-JP'
  return 'en-US'
}

export function money(cents: number, locale = activeLocale(), currency = 'USD') {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100)
}

export function shortDate(value: string | Date, locale = activeLocale()) {
  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(value))
}

export function dateTime(value: string | Date, locale = activeLocale()) {
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  }).format(new Date(value))
}

export function time(value: string | Date, locale = activeLocale()) {
  return new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(new Date(value))
}

export function smartDay(value: string | Date, locale = activeLocale()) {
  const date = new Date(value)
  if (isToday(date)) return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(0, 'day')
  if (isTomorrow(date)) return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(1, 'day')
  return new Intl.DateTimeFormat(locale, { weekday: 'long', month: 'short', day: 'numeric' }).format(date)
}

export function relative(value: string | Date, locale = activeLocale()) {
  const milliseconds = new Date(value).getTime() - Date.now()
  const seconds = milliseconds / 1_000
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 31_536_000], ['month', 2_592_000], ['week', 604_800], ['day', 86_400],
    ['hour', 3_600], ['minute', 60], ['second', 1],
  ]
  const [unit, divisor] = units.find(([, size]) => Math.abs(seconds) >= size) ?? units.at(-1)!
  return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(Math.round(seconds / divisor), unit)
}

export function fullAddress(record: {
  address_line1: string | null
  address_line2?: string | null
  city: string | null
  region: string | null
  postal_code: string | null
}) {
  return [record.address_line1, record.address_line2, record.city, record.region, record.postal_code]
    .filter(Boolean)
    .join(', ')
}

export const appointmentStatusLabel: Record<AppointmentStatus, string> = {
  planned: 'Planned',
  awaitingBusiness: 'Needs business approval',
  awaitingClient: 'Waiting for client',
  awaitingClientChoice: 'Client choosing a time',
  awaitingBusinessAfterClientChoice: 'Review client choice',
  confirmed: 'Confirmed',
  inProgress: 'In progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
}

export const invoiceStatusLabel: Record<InvoiceStatus, string> = {
  draft: 'Draft', sent: 'Sent', due: 'Due', overdue: 'Overdue', paid: 'Paid', cancelled: 'Cancelled',
}

export function statusTone(status: AppointmentStatus | InvoiceStatus) {
  if (status === 'confirmed' || status === 'paid' || status === 'completed') return 'success'
  if (status === 'cancelled' || status === 'overdue') return 'danger'
  if (status === 'awaitingBusiness' || status === 'awaitingBusinessAfterClientChoice' || status === 'due') return 'warning'
  if (status === 'planned' || status === 'draft') return 'neutral'
  return 'info'
}
